import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, PropertyValueMap, css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

import { QuantityPerSizePerColorStepDto } from '@/api-sdk';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, loadingStyles, quantityTableStyles } from '@/styles';

import '../../../configuration/hsp/fg-hsp-label';
import './fg-quantity-table-quantity-input';

const SHOW_QUANTITY_INCREMENT_BUTTONS_TRESHOLD = 1;

@customElement('fg-quantity-per-size-per-color-table')
@localized()
export class FgQuantityPerSizePerColorTable extends LitElement {
  @property({ type: Array, attribute: 'colors' })
  colors: QuantityPerSizePerColorStepDto['colors'] = [];

  @property({ type: Array })
  options: QuantityPerSizePerColorStepDto['options'] = [];

  @property({ type: Number })
  totalQuantity = 0;

  @property({ type: Number })
  hspMaximumQuantity?: number | null = 0;

  @property({ type: String })
  totalPrice?: string | null = null;

  @property({ type: Boolean })
  loading = false;

  @state()
  showQuantityIncrementButtons = true;

  @state()
  multiSelect = false;

  @query('.wrapper.color')
  colorWrapper?: HTMLElement;

  updated() {
    this.style.setProperty(
      '--wrapper-column-count',
      this.colors.length.toString(),
    );

    this.style.setProperty(
      '--wrapper-height',
      this.colorWrapper?.offsetHeight && this.colorWrapper?.offsetHeight > 0
        ? `${this.colorWrapper?.offsetHeight}px`
        : '66px',
    );
  }

  protected willUpdate(_changedProperties: PropertyValueMap<any>) {
    if (_changedProperties.has('colors')) {
      this.multiSelect = this.colors.length > 1;
    }
  }

  render() {
    return html`
      <table class="quantities">
        <tr>
          <td>
            <div class="split">
              <div class="left">
                <table>
                  <thead>
                    <tr>
                      <th class="amount">${t('labels.quantity')}</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>
                        <span class="wrapper color">
                          ${this.colors.map(colorItem => {
                            const { color, image } = colorItem;

                            return html`
                              <div
                                class=${this.multiSelect ? 'multi-select' : ''}
                              >
                                ${image
                                  ? html`
                                      <div class="image">
                                        <img
                                          src="${image}"
                                          alt="${color}"
                                          width="50"
                                          height="50"
                                        />
                                      </div>
                                    `
                                  : nothing}

                                <span>${color}</span>
                              </div>
                            `;
                          })}
                        </span>
                      </td>
                    </tr>

                    ${this.options.map(option => {
                      const { quantities } = option;

                      if (
                        quantities.length >
                        SHOW_QUANTITY_INCREMENT_BUTTONS_TRESHOLD
                      ) {
                        this.showQuantityIncrementButtons = false;
                      }

                      return html`
                        <tr class="options">
                          <td class="amount">
                            <span class="wrapper">
                              ${quantities.map(quantityItem => {
                                const {
                                  disabled,
                                  queryString,
                                  value: quantity,
                                } = quantityItem;

                                return html`
                                  <fg-quantity-table-quantity-input
                                    optionQueryString=${queryString}
                                    .quantity=${quantity}
                                    .disabled=${disabled}
                                    .showQuantityIncrementButtons=${this
                                      .showQuantityIncrementButtons}
                                    .multiSelect=${this.multiSelect}
                                  ></fg-quantity-table-quantity-input>
                                `;
                              })}
                            </span>
                          </td>
                        </tr>
                      `;
                    })}
                  </tbody>
                </table>
              </div>

              <div class="right">
                <table>
                  <thead>
                    <tr>
                      <th class="size">${t('labels.size')}</th>
                      <th class="mobile-hidden">${t('labels.pricePerItem')}</th>
                      <th class="mobile-show">${t('labels.pricePerItemMb')}</th>
                      <th>${t('labels.total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>&nbsp;</td>
                      <td>&nbsp;</td>
                      <td>&nbsp;</td>
                    </tr>
                    ${this.options.map(
                      option => html`
                        <tr class=${this.loading ? 'loading' : ''}>
                          <td class="size">${option.size}</td>
                          <td><span>${option.unitPrice}</span></td>
                          <td class="total-price">
                            <span>${option.totalPrice ?? '-'}</span>
                          </td>
                        </tr>
                      `,
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </td>
        </tr>

        <tfoot>
          <tr class=${this.loading ? 'loading' : ''}>
            <td>
              <table>
                <tr class="total">
                  <td>
                    ${t(
                      'content.configurator.chooseQuantityStep.totalQuantity',
                      {
                        quantity: this.totalQuantity,
                      },
                    )}
                  </td>
                  <td><span>${this.totalPrice ?? '-'}</span></td>
                </tr>
                ${this.hspMaximumQuantity
                  ? html`
                      <tr>
                        <td>
                          <fg-hsp-label
                            .hspMaximumQuantity=${this.hspMaximumQuantity}
                          ></fg-hsp-label>
                        </td>
                        <td></td>
                      </tr>
                    `
                  : nothing}
              </table>
            </td>
          </tr>
        </tfoot>
      </table>
    `;
  }

  static styles = [
    baseStyles,
    quantityTableStyles,
    loadingStyles,
    css`
      .mobile-hidden {
        display: none;
      }
      .mobile-show {
        display: table-cell;
      }

      .quantities {
        table-layout: fixed;
      }

      .quantities > tbody {
        background: transparent;
      }

      .quantities tbody > tr > td {
        border: 0;
        padding: 0;
      }

      .split {
        background: #fff;
        display: flex;
        justify-content: space-between;
        border-top-left-radius: var(
          --fg-borderradius,
          var(--fg-fallback-borderradius)
        );
        border-top-right-radius: var(
          --fg-borderradius,
          var(--fg-fallback-borderradius)
        );
      }

      .split table {
        width: 100%;
      }

      .split table thead th {
        background: var(--fg-gray--100, var(--fg-fallback-gray--100));
        font-size: 0.875rem;
        white-space: nowrap;
      }

      .split table tbody tr:not(.options) td {
        border-bottom: 1px solid
          var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .split .left table tr.options {
        border-bottom: 1px solid
          var(--fg-gray--300, var(--fg-fallback-gray--300));
        width: 100%;
      }

      .split table tbody tr td {
        padding-right: 10px;
        padding-left: 10px;
      }

      .split table tbody tr:first-child td {
        border: 0;
        padding-right: 0;
      }

      .split .left {
        overflow-x: auto;
        flex-grow: 1;
      }

      .split .left table {
        display: grid;
      }

      .split .left table tr,
      .split .left table th {
        display: block;
      }

      .split .left,
      .split .right {
        position: relative;
      }

      .split .left::before,
      .split .right::before {
        content: '';
        height: calc(100% - 32px);
        position: absolute;
        top: 32px;
        width: 10px;
        z-index: 1;
      }

      .split .left::before {
        background: linear-gradient(
          270deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 1) 100%
        );
        left: 0;
      }

      .split .right::before {
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 1) 100%
        );
        left: -10px;
      }

      .split .left table tbody {
        display: block;
        max-width: 100%;
        overflow-x: auto;
        position: relative;
      }

      .split .left table thead th:first-child {
        border-top-left-radius: var(
          --fg-borderradius,
          var(--fg-fallback-borderradius)
        );
      }

      .split .right table thead th:last-child {
        border-top-right-radius: var(
          --fg-borderradius,
          var(--fg-fallback-borderradius)
        );
      }

      .split .left {
        width: 45%;
      }
      .split .right {
        width: 55%;
        overflow-x: scroll;
      }

      .split .right table tr td {
        height: 46px;
      }

      .split .right table tbody tr:first-child td {
        height: var(--wrapper-height);
      }

      .wrapper {
        display: grid;
        grid-template-columns: repeat(var(--wrapper-column-count), 1fr);
        justify-items: center;
        gap: 10px;
      }

      .wrapper div {
        width: 50px;
        font-size: 0.875rem;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      .wrapper div.multi-select {
        width: 60px;
      }

      .wrapper .image {
        width: 50px;
        height: 50px;
      }

      .wrapper .image img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      .color .image + span {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        width: 50px;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .split .left,
        .split .right {
          width: 50%;
        }

        .mobile-hidden {
          display: table-cell;
        }

        .mobile-show {
          display: none;
        }

        .split table tbody tr td {
          padding-right: 20px;
          padding-left: 20px;
        }
      }

      @media screen and (max-width: ${BREAKPOINT_SMALL_PX}px) {
        .split .right {
          overflow-x: auto;
        }
      }
    `,
  ];
}
